import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { PERMISSION_SCHEMA, ROLE_PRESETS } from '../permissions';
import { UserPlus, Trash2, KeyRound, Shield, Edit3, X, Check, Users } from 'lucide-react';
import StatTile from '../components/StatTile';

const StatCard = StatTile;

const ROLE_DEFAULTS = {
  admin: null,
  supervisor: {
    live_map: { view: true }, invoices: { view: true, upload: false, delete: false },
    tracking: { view: true },
    route_planner: { view: true, optimize: false, assign: false },
    drivers: { view: true, edit: false, delete: false },
    driver_reports: { view: true, delete: false }, odometer_report: { view: true }, journey_history: { view: true },
    analytics: { view: true }, management_report: { view: true }, rescheduled: { view: true, assign: false },
    settings: { view: false }, users: { view: false },
  },
  viewer: {
    live_map: { view: true }, invoices: { view: false }, tracking: { view: false }, route_planner: { view: false },
    drivers: { view: false }, driver_reports: { view: true }, odometer_report: { view: true }, journey_history: { view: true },
    analytics: { view: true }, management_report: { view: true }, rescheduled: { view: false }, settings: { view: false }, users: { view: false },
  },
};

const DRIVER_TYPE_OPTIONS = [
  { value: 'delivery', label: 'Delivery Driver' },
  { value: 'presalesman', label: 'Presalesman' },
  { value: 'van_salesman', label: 'Van Salesman' },
];

const initials = (name) => (name || '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const roleColors = { admin: ['#7c3aed', '#f3e8ff'], supervisor: ['#0284c7', '#e0f2fe'], viewer: ['#64748b', '#f1f5f9'] };

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'viewer', permissions: {}, driver_scope: [] });
  const [pwModal, setPwModal] = useState(null);
  const [newPw, setNewPw] = useState('');

  const load = () => api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => { setUsers([]); toast.error('Failed to load users'); });
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditUser(null);
    setForm({ name: '', email: '', password: '', role: 'viewer', permissions: JSON.parse(JSON.stringify(ROLE_DEFAULTS.viewer)), driver_scope: [] });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name, email: u.email, password: '', role: u.role,
      permissions: u.role === 'admin' ? {} : (u.permissions || JSON.parse(JSON.stringify(ROLE_DEFAULTS[u.role] || ROLE_DEFAULTS.viewer))),
      driver_scope: u.driver_scope || []
    });
    setShowModal(true);
  };

  const onRoleChange = (role) => {
    setForm(f => ({ ...f, role, permissions: role === 'admin' ? {} : JSON.parse(JSON.stringify(ROLE_DEFAULTS[role] || ROLE_DEFAULTS.viewer)) }));
  };

  const togglePerm = (page, action) => {
    setForm(f => {
      const perms = JSON.parse(JSON.stringify(f.permissions));
      if (!perms[page]) perms[page] = {};
      perms[page][action] = !perms[page][action];
      // If turning off view, turn off all other actions for that page
      if (action === 'view' && !perms[page][action]) {
        Object.keys(perms[page]).forEach(a => perms[page][a] = false);
      }
      // If turning on any action, ensure view is on
      if (action !== 'view' && perms[page][action]) perms[page].view = true;
      return { ...f, permissions: perms };
    });
  };

  const toggleDriverScope = (type) => {
    setForm(f => {
      const scope = f.driver_scope.includes(type)
        ? f.driver_scope.filter(t => t !== type)
        : [...f.driver_scope, type];
      return { ...f, driver_scope: scope };
    });
  };

  const save = async () => {
    if (!form.name || !form.email) return toast.error('Name and email required');
    if (!editUser && (!form.password || form.password.length < 6)) return toast.error('Password min 6 characters');
    try {
      const payload = {
        name: form.name, email: form.email, role: form.role,
        permissions: form.role === 'admin' ? null : form.permissions,
        driver_scope: form.role === 'admin' ? null : form.driver_scope,
      };
      if (editUser) {
        await api.put('/users/' + editUser.id, payload);
        toast.success('User updated');
      } else {
        await api.post('/users', { ...payload, password: form.password });
        toast.success('User created');
      }
      setShowModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put('/users/' + u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'User disabled' : 'User enabled');
      load();
    } catch { toast.error('Failed'); }
  };

  const resetPw = async () => {
    if (!newPw || newPw.length < 6) return toast.error('Password min 6 characters');
    try {
      await api.post('/users/' + pwModal.id + '/password', { password: newPw });
      toast.success('Password reset');
      setPwModal(null); setNewPw('');
    } catch { toast.error('Failed'); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete('/users/' + u.id);
      toast.success('User deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const roleBadge = (role) => {
    const [c, bg] = roleColors[role] || roleColors.viewer;
    return <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: c, background: bg, textTransform: 'capitalize' }}>{role}</span>;
  };

  const isCustomPerms = (u) => u.role !== 'admin' && u.permissions && JSON.stringify(u.permissions) !== JSON.stringify(ROLE_DEFAULTS[u.role] || ROLE_DEFAULTS.viewer);

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.is_active !== false).length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const customCount = users.filter(isCustomPerms).length;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Users size={22} color="#0284c7" />
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>User Management</h1>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Add supervisors and viewers with custom permissions</p>
          </div>
        </div>
        <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
          <UserPlus size={16} /> Add User
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard icon={Users} label="Total users" value={totalUsers} color="#0284c7" sub="All accounts" />
        <StatCard icon={Check} label="Active" value={activeUsers} color="#16a34a" sub={`${totalUsers - activeUsers} disabled`} />
        <StatCard icon={Shield} label="Admins" value={adminCount} color="#7c3aed" sub="Full access" />
        <StatCard icon={Edit3} label="Custom permissions" value={customCount} color="#f59e0b" sub="Differ from role default" />
      </div>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {(Array.isArray(users) ? users : []).map(u => {
          const [ac, abg] = roleColors[u.role] || roleColors.viewer;
          const custom = isCustomPerms(u);
          return (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #f1f5f9', gap: 12, opacity: u.is_active === false ? 0.5 : 1 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: abg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: ac, fontWeight: 700, fontSize: 14 }}>
                {initials(u.name)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>{u.name} {u.id === 1 && <span style={{ fontSize: 10, color: '#7c3aed' }}>★ Master</span>}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{u.email}</div>
                {u.driver_scope && u.driver_scope.length > 0 && (
                  <div style={{ fontSize: 11, color: '#0284c7', marginTop: 2 }}>
                    Scoped to: {u.driver_scope.map(t => (DRIVER_TYPE_OPTIONS.find(o => o.value === t) || {}).label || t).join(', ')}
                  </div>
                )}
              </div>
              {roleBadge(u.role)}
              {custom && <span title="Permissions customized from the role default" style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 10 }}>Custom</span>}
              {u.is_active === false && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Disabled</span>}
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => openEdit(u)} title="Edit" style={iconBtn}><Edit3 size={15} /></button>
                <button onClick={() => setPwModal(u)} title="Reset password" style={iconBtn}><KeyRound size={15} /></button>
                {u.id !== 1 && (
                  <>
                    <button onClick={() => toggleActive(u)} title={u.is_active === false ? 'Enable' : 'Disable'} style={iconBtn}>{u.is_active === false ? <Check size={15} /> : <X size={15} />}</button>
                    <button onClick={() => del(u)} title="Delete" style={{ ...iconBtn, color: '#dc2626' }}><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{editUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inp} />
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inp} />
              </div>
            </div>

            {!editUser && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Password</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={inp} placeholder="Min 6 characters" />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Role</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['admin', 'supervisor', 'viewer'].map(r => (
                  <button key={r} onClick={() => onRoleChange(r)} style={{
                    flex: 1, padding: '10px', borderRadius: 8, border: form.role === r ? '2px solid #0284c7' : '1px solid #e2e8f0',
                    background: form.role === r ? '#eff6ff' : '#fff', cursor: 'pointer', textAlign: 'left'
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'capitalize', color: '#1e293b' }}>{r}</div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{ROLE_PRESETS[r]}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.role !== 'admin' && (
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Driver type access</label>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {DRIVER_TYPE_OPTIONS.map(({ value, label }) => (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#1e293b', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.driver_scope.includes(value)} onChange={() => toggleDriverScope(value)} />
                      {label}
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  Leave all unchecked to see every driver type. Check one or more to restrict this user to only those driver types across Drivers, Driver Reports and Analytics.
                </p>
              </div>
            )}

            {form.role !== 'admin' && (
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Permissions</label>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  {PERMISSION_SCHEMA.map(({ page, label, actions }) => (
                    <div key={page} style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', borderBottom: '1px solid #f1f5f9', gap: 10 }}>
                      <div style={{ flex: 1, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{label}</div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        {actions.map(action => (
                          <label key={action} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b', cursor: 'pointer', textTransform: 'capitalize' }}>
                            <input type="checkbox" checked={!!(form.permissions[page] && form.permissions[page][action])} onChange={() => togglePerm(page, action)} />
                            {action}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Turning off "view" disables all other actions for that page.</p>
              </div>
            )}

            {form.role === 'admin' && (
              <div style={{ background: '#f3e8ff', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#7c3aed' }}>
                <Shield size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                Admins have full access to everything including user management.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={save} style={{ padding: '10px 18px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>{editUser ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset modal */}
      {pwModal && (
        <div style={overlay}>
          <div style={modal}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Reset Password</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>New password for {pwModal.name}</p>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={inp} placeholder="Min 6 characters" />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => { setPwModal(null); setNewPw(''); }} style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={resetPw} style={{ padding: '10px 18px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const iconBtn = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 7, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modal = { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
